import React, { useEffect, useState, useRef } from "react";
import Button from 'react-bootstrap/Button';
import Image from 'react-bootstrap/Image';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Sidebar from "./Sidebar";
import HomePricing from "./HomePricing";
import axios from "axios"
import ChartComponent from "./FollowerChart";

const Visualizer = () => {
	const [username, setUsername] = useState('');
	const [platform, setPlatform] = useState('');
	const [profileData, setProfileData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [displayedFollowers, setDisplayedFollowers] = useState(0);
	const animationRef = useRef(null);
	const [searchResults, setSearchResults] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResultsVisible, setSearchResultsVisible] = useState(false);
	const searchTimeoutRef = useRef(null);
	const fetchTimeoutRef = useRef(null);

	const fetchProfileData = async (username, platform) => {
		if (!username || !platform) {
			setError('Please enter a username and select a platform');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await axios.get(`https://api.viralgpt.com/auth/getProfile/${platform}/${username}`);
			setProfileData(response.data);
			// Start the follower count animation
			setDisplayedFollowers(0);
			startCountAnimation(response.data.followers);
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to fetch profile data');
			setProfileData(null); // Clear profile data on error
		} finally {
			setLoading(false);
		}
	};

	const debouncedFetchProfileData = () => {
		if (fetchTimeoutRef.current) {
			clearTimeout(fetchTimeoutRef.current);
		}

		fetchTimeoutRef.current = setTimeout(() => {
			fetchProfileData(username, platform);
		}, 500); // 500ms delay
	};

	const startCountAnimation = (targetFollowers) => {
		// Cancel any existing animation
		if (animationRef.current) {
			cancelAnimationFrame(animationRef.current);
		}

		const duration = 1000; // Animation duration in ms
		const start = performance.now();
		const startValue = 0;

		const animate = (currentTime) => {
			const elapsed = currentTime - start;
			const progress = Math.min(elapsed / duration, 1);

			// Easing function for smooth animation
			const easeOutQuad = 1 - Math.pow(1 - progress, 2);
			
			const currentValue = Math.floor(startValue + (targetFollowers - startValue) * easeOutQuad);
			setDisplayedFollowers(currentValue);

			if (progress < 1) {
				animationRef.current = requestAnimationFrame(animate);
			}
		};

		animationRef.current = requestAnimationFrame(animate);
	};

	const calculateProjectedGrowth = () => {
		if (!profileData?.followers) return 0;
		// Calculate 2.5x growth projection
		return Math.floor(profileData.followers + 3000);
	};

	const handleYouTubeSearch = async (query) => {
		if (!query) {
			setSearchResults([]);
			return;
		}
		try {
			const response = await axios.post('https://api.viralgpt.com/auth/search', {
				platform: 'youtube',
				username: query
			});
			setSearchResults(response.data.data);
		} catch (err) {
			console.error('Error searching YouTube channels:', err);
		}
	};

	// Debounced YouTube search
	const debouncedYouTubeSearch = (query) => {
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		searchTimeoutRef.current = setTimeout(() => {
			handleYouTubeSearch(query);
		}, 500); // 500ms delay
	};

	useEffect(() => {
		if (platform && username && platform !== 'youtube') {
			debouncedFetchProfileData();
		}
		
		// Cleanup animation and timeouts on unmount
		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current);
			}
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
			if (fetchTimeoutRef.current) {
				clearTimeout(fetchTimeoutRef.current);
			}
		};
	}, [platform, username]);

	return (
		<>
		<div id="visualizer"></div>
		<div className="container mx-auto px-4 py-8 my-20 card">
			
				<h2 className="text-3xl font-bold text-center mb-6">Preview Your Social Media Growth</h2>
				<div className="text-center mb-5">
					<span className="bg-instagram text-white px-4 py-2 rounded-full text-lg font-bold">3,000+ New Followers Per Month</span>
				</div>
				<p className="text-center text-gray-600 mb-8 text-lg">Enter your username to see how our AI tools can help grow your following.</p>
				
				<div className="mb-6 mx-auto max-w-md w-full">
					<input 
						type="text"
						placeholder="Enter your username"
						className="w-full p-3 border rounded-lg"
						value={platform === 'youtube' ? searchQuery || username : username}
						onChange={(e) => {
							if (platform === 'youtube') {
								setSearchQuery(e.target.value);
								debouncedYouTubeSearch(e.target.value);
							} else {
								setUsername(e.target.value);
							}
						}}
					/>
					{platform === 'youtube' && (
						<button
							className="w-full mt-2 p-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
							onClick={() => handleYouTubeSearch(searchQuery)}
						>
							Search YouTube Channels
						</button>
					)}
					{platform === 'youtube' && searchResults?.length > 0 && (
						<div className="border rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 overflow-y-auto max-h-[180px]">
							{searchResults.map((channel) => (
								<div
									key={channel.id}
									className="p-3 border rounded-lg hover:bg-gray-100 cursor-pointer flex-shrink-0"
									onClick={() => {
										setUsername(channel.title);
										setSearchQuery(channel.title);
										setSearchResults([]);
										// Wait for state update before fetching
										fetchProfileData(channel.channelId, 'youtube');
									}}
								>
									<div className="flex flex-col items-center text-center">
										<img 
											src={channel.thumbnail?.[0]?.url} 
											alt={channel.title}
											className="w-10 h-10 rounded-full mb-1"
										/>
										<span className="font-medium text-sm truncate w-full">{channel.title}</span>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
				<div className="mb-8 flex justify-center space-x-4">
					<button 
						className={`p-4 border rounded-lg hover:bg-gray-100 transition-colors ${platform === 'instagram' ? 'bg-gray-100' : ''}`}
						onClick={() => {
							setPlatform('instagram');
							setSearchQuery('');
							setProfileData(null);
							setSearchResults([]);
						}}
					>
						<i className="fab fa-instagram text-2xl text-instagram"></i>
					</button>
					<button 
						className={`p-4 border rounded-lg hover:bg-gray-100 transition-colors ${platform === 'tiktok' ? 'bg-gray-100' : ''}`}
						onClick={() => {
							setPlatform('tiktok');
							setSearchQuery('');
							setProfileData(null);
							setSearchResults([]);
						}}
					>
						<i className="fab fa-tiktok text-2xl text-dark"></i>
					</button>
					<button 
						className={`p-4 border rounded-lg hover:bg-gray-100 transition-colors ${platform === 'x' ? 'bg-gray-100' : ''}`}
						onClick={() => {
							setPlatform('x');
							setSearchQuery('');
							setProfileData(null);
							setSearchResults([]);
						}}
					>
						<i className="fab fa-x-twitter text-2xl text-twitter"></i>
					</button>
					<button 
						className={`p-4 border rounded-lg hover:bg-gray-100 transition-colors ${platform === 'youtube' ? 'bg-gray-100' : ''}`}
						onClick={() => {
							setPlatform('youtube');
							setUsername('');
							setSearchResults([]);
							setProfileData(null);
						}}
					>
						<i className="fab fa-youtube text-2xl text-red-600"></i>
					</button>
				</div>

				{error && (
					<div className="text-red-500 text-center mb-4">{error}</div>
				)}

				{loading ? (
					<div className="text-center">Loading...</div>
				) : profileData && (
					<>
					<center>
						<div className="border rounded-lg p-6 bg-white shadow max-w-screen-md">
							<div className="flex flex-col items-center space-y-4 mb-8">
								<div className="w-24 h-24 rounded-full flex-shrink-0">
									<img 
										src={platform === 'instagram' 
											? `https://rizzup.net/proxy/${encodeURIComponent(profileData.photo)}` 
											: profileData.photo || "/images/default.png"
										}
										alt="Profile"
										className="w-full h-full rounded-full object-cover"
									/>
									
								</div>
								<div className="w-full">
									<h3 className="font-bold text-xl sm:text-2xl mb-3 text-center">
										<div className="flex items-center justify-center">
											<i className={`fab fa-${platform === 'x' ? 'x-twitter' : platform} mr-2 ${
												platform === 'instagram' ? 'text-instagram' :
												platform === 'tiktok' ? 'text-dark' :
												platform === 'x' ? 'text-twitter' :
												platform === 'youtube' ? 'text-red-600' : ''
											}`}></i>
											{profileData.username}
										</div>
									</h3>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
										<div className="text-center p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 bg-gradient-to-b from-white to-gray-50 relative">
											<img src="/images/logo-icon.png" alt="ViralGPT Logo" className="absolute -top-3 -right-3 w-6 h-6 opacity-20" />
											<div className="text-gray-600 text-sm font-medium uppercase tracking-wide mb-2">
												Before
											</div>
											<div className="font-bold text-xl sm:text-2xl text-gray-800">
												{profileData.followers.toLocaleString()}
											</div>
											<div className="text-sm text-gray-500 mt-1 font-medium">
												Current Followers
											</div>
										</div>

										<div className="text-center p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-instagram-start to-instagram-end border border-purple-200 relative">
											<img src="/images/logo-icon.png" alt="ViralGPT Logo" className="absolute -top-3 -right-3 w-6 h-6" />
											<div className={`text-sm font-bold uppercase tracking-wide mb-2 text-${platform}-dark`}>
												After
											</div>
											<div className={`font-bold text-xl sm:text-2xl bg-${platform} text-white`}>
												{(displayedFollowers + 3000).toLocaleString()}
											</div>
											<div className={`text-sm font-bold mt-1 text-${platform}-dark`}>
												New Followers
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="border-t pt-6">
								<h4 className="font-bold text-gray-800 mb-2">
									Projected Growth
								</h4>
								<p className="text-gray-700 mb-6">
									Using ViralGPT's AI-powered strategies, you could gain up to
									<span className="font-bold"> {calculateProjectedGrowth().toLocaleString()} </span>
									new followers in the next 30 days!
								</p>

								<ChartComponent 
									followers={profileData.followers}
									growthRate="100"
									days={30}
								/>
							</div>
						</div>
						</center>
					</>
				)}
			</div>
			</>
	);
};

export default Visualizer;